<template>
	<BlockVisibilityObserver title="WHY_KOVA">
		<section class="kova-v2-benefits">
			<div class="kova-v2-benefits__card" :class="{ 'kova-v2-benefits__card_centered': centered }">
				<!-- Illustration: decorative lines, a static sample reminder (never user data) and
				     the photo. Hidden from assistive tech as a whole — it is artwork. -->
				<div aria-hidden="true" class="kova-v2-benefits__visual">
					<img
						alt=""
						class="kova-v2-benefits__mark"
						decoding="async"
						height="680"
						loading="lazy"
						:src="mark"
						width="602"
					/>

					<div class="kova-v2-benefits__notification">
						<span class="kova-v2-benefits__tail" />
						<span class="kova-v2-benefits__thumb">
							<img alt="" height="248" decoding="async" loading="lazy" :src="vial" width="200" />
						</span>
						<span class="kova-v2-benefits__note">
							<span class="kova-v2-benefits__note-title">Time for injection</span>
							<span class="kova-v2-benefits__note-text">
								Jane, it’s time for your GLP-1 injection. You’ve got this!
							</span>
						</span>
					</div>

					<!-- One photo crop at two resolutions: 274px wide on mobile, 604px on desktop. -->
					<img
						alt=""
						class="kova-v2-benefits__photo"
						decoding="async"
						height="726"
						loading="lazy"
						sizes="(min-width: 1024px) 604px, 274px"
						:src="phoneMobile"
						:srcset="`${phoneMobile} 548w, ${phone} 1208w`"
						width="548"
					/>
					<span class="kova-v2-benefits__fade" />
				</div>

				<h2 class="kova-v2-benefits__title">{{ title }}</h2>

				<ul class="kova-v2-benefits__list">
					<li v-for="item in items" :key="item" class="kova-v2-benefits__item">
						<img
							alt=""
							class="kova-v2-benefits__bullet"
							height="16"
							decoding="async"
							loading="lazy"
							:src="star"
							width="16"
						/>
						<span>{{ item }}</span>
					</li>
				</ul>

				<ul class="kova-v2-benefits__chips">
					<li v-for="chip in chips" :key="chip.label" class="kova-v2-benefits__chip">
						<img
							alt=""
							class="kova-v2-benefits__chip-icon"
							height="16"
							decoding="async"
							loading="lazy"
							:src="chip.icon === 'shipping' ? delivery : star"
							width="16"
						/>
						<span>{{ chip.label }}</span>
					</li>
				</ul>

				<!-- 6547:17231 — desktop-only CTA in the Why Kova block. -->
				<LandingKovaV2Button
					class="kova-v2-benefits__cta"
					:label="ctaLabel"
					variant="section"
					@click="emit('progressLoaderCompleted', 'why_kova_eligible')"
				/>
			</div>
		</section>
	</BlockVisibilityObserver>
</template>

<script setup lang="ts">
import BlockVisibilityObserver from '@/components/BlockVisibilityObserver.vue'
import LandingKovaV2Button from '@/modules/Landing/components/v2/LandingKovaV2Button.vue'
import { KovaV2CtaEntryPoint, LandingComponentProps } from '@/modules/Landing/instances/@types/landing-types'

import delivery from '@/assets/kova/landing/v2/icon-fast-delivery.svg'
import mark from '@/assets/kova/landing/v2/why-kova-mark.svg'
import phone from '@/assets/kova/landing/v2/why-kova-phone.webp'
import phoneMobile from '@/assets/kova/landing/v2/why-kova-phone-mobile.webp'
import star from '@/assets/kova/landing/v2/icon-one-star.svg'
import vial from '@/assets/kova/landing/v2/why-kova-notification-vial.webp'

defineOptions({ name: 'LandingKovaV2Benefits' })

defineProps<LandingComponentProps.KovaV2Benefits>()

const emit = defineEmits<{ progressLoaderCompleted: [entryPoint: KovaV2CtaEntryPoint] }>()
</script>

<style lang="scss" scoped>
// Mobile (6415:53638): 8 top, 32 bottom, 16px gutters around the blue card.
.kova-v2-benefits {
	padding: var(--size-8) var(--size-16) var(--size-32);

	font-family: var(--kova-lp-font);
}

// 6418:54090 — #B2EBFF, 32 radius, 20px sides, 24 bottom; the visual runs edge to edge.
.kova-v2-benefits__card {
	overflow: hidden;

	display: flex;
	flex-direction: column;
	align-items: flex-start;
	padding: 0 var(--size-20) var(--size-24);

	border-radius: var(--size-32);
	background: var(--kova-lp-cta-panel);
}

// 6418:54092 — 248px tall, as wide as the card (358), everything placed in its frame.
.kova-v2-benefits__visual {
	position: relative;
	overflow: hidden;
	align-self: stretch;
	height: 248px;
	margin-inline: calc(-1 * var(--size-20));

	img {
		display: block;
		max-width: none;
	}
}

// 6418:54094 — the Kova mark in #C9F1FF, 255x288 at (-36,-116); one vector, drawn at
// 602x680 on desktop (6557:32812).
.kova-v2-benefits__mark {
	position: absolute;
	left: -36px;
	top: -116px;
	width: 254.99px;
	height: 288px;
}

// 6420:54141 — white 200x56 reminder at (20,69), behind the photo.
.kova-v2-benefits__notification {
	position: absolute;
	left: var(--size-20);
	top: 69px;
	z-index: 0; // Keep the reminder's children below the photo that follows it.

	display: flex;
	align-items: center;
	gap: var(--size-6);
	box-sizing: border-box;
	width: 200px;
	height: var(--size-56);
	padding-right: var(--size-16);

	border-radius: var(--size-16);
	background: var(--Fill-White);
}

// 6420:54152 — the thought-bubble tail, flipped, hanging under the card.
// 6420:54152 — the thought-bubble tail under the card: Figma's union of two white
// circles (24px, and 8px further out), flipped down — drawn as two round spans.
.kova-v2-benefits__tail {
	position: absolute;
	left: 102px;
	top: 38px;
	width: 26px;
	height: 31px;

	&::before,
	&::after {
		content: '';
		position: absolute;

		border-radius: 50%;
		background: var(--Fill-White);
	}

	&::before {
		left: 0;
		top: 0;
		width: 24px; // the bubble is drawn in fixed px, like its 26x31 box
		height: 24px;
	}

	&::after {
		left: 18px;
		top: 23px;
		width: 8px;
		height: 8px;
	}
}

// 6420:54142 — 46x54 slot; the vial 39.45x48.97 just off its centre.
.kova-v2-benefits__thumb {
	position: relative;
	z-index: 1;
	flex-shrink: 0;
	width: 46px;
	height: var(--size-54);

	img {
		position: absolute;
		left: calc(50% + 3.33px);
		top: calc(50% - 1.41px);
		width: 39.45px;
		height: 48.97px;
		object-fit: cover;
		transform: translate(-50%, -50%);
	}
}

// The tail is drawn first in Figma, so the copy paints over it.
.kova-v2-benefits__note {
	position: relative;
	z-index: 1;

	display: flex;
	flex: 1 1 0;
	flex-direction: column;
	gap: var(--size-4);
	min-width: 0;
}

.kova-v2-benefits__note-title {
	font-size: var(--size-10);
	font-weight: 500;
	line-height: var(--size-12);

	color: var(--kova-lp-ink);
}

.kova-v2-benefits__note-text {
	font-size: var(--size-8); // 6420:54146
	line-height: var(--size-11);

	color: var(--kova-v2-notification);
}

// 6547:4108 — 274x363 at (86,-11).
.kova-v2-benefits__photo {
	position: absolute;
	left: 86px;
	top: -11px;
	width: 274px;
	height: 363px;
	object-fit: cover;
}

// 6418:54101 — 48px fade from clear to the card colour along the bottom edge.
.kova-v2-benefits__fade {
	position: absolute;
	left: 0;
	right: 0;
	bottom: 0;
	height: 48px; // fixed like the photo it fades — the rem ladder shrinks under 376px

	background: linear-gradient(transparent, var(--kova-lp-cta-panel));
}

// 6418:54102 — 28 / 1.16, 16px under the visual.
.kova-v2-benefits__title {
	margin: var(--size-16) 0 0;

	font-size: var(--size-28);
	font-weight: 500;
	line-height: var(--size-32); // 28px × 1.16 = 32.48px — exact size not in design system
	letter-spacing: -0.56px;

	color: var(--kova-lp-ink);
}

.kova-v2-benefits__list {
	align-self: stretch;

	display: flex;
	flex-direction: column;
	gap: var(--size-6); // 6418:54103
	margin: var(--size-16) 0 0;
	padding: 0;

	list-style: none;
}

.kova-v2-benefits__item {
	display: flex;
	align-items: center;
	gap: var(--size-8);

	font-size: var(--size-14);
	font-weight: 500;
	line-height: var(--size-20); // 14px × 1.4 = 19.60px — exact size not in design system

	color: var(--kova-lp-ink);
}

.kova-v2-benefits__bullet {
	flex-shrink: 0;
	display: block;
	width: var(--size-16);
	height: var(--size-16);
}

// 6566:40602 — outlined chips, 24px under the list.
.kova-v2-benefits__chips {
	display: flex;
	flex-wrap: wrap;
	gap: var(--size-8);
	margin: var(--size-24) 0 0;
	padding: 0;

	list-style: none;
}

.kova-v2-benefits__chip {
	display: flex;
	align-items: center;
	gap: var(--size-4);
	padding: calc(var(--size-4) - 1px) calc(var(--size-12) - 1px); // 4/12 minus the inside 1px stroke

	font-family: var(--font-family);
	font-size: var(--size-14);
	line-height: var(--size-20);
	white-space: nowrap;

	border: 1px solid var(--Stroke-Secondary);
	border-radius: var(--size-8);
	color: var(--kova-lp-ink);
}

.kova-v2-benefits__chip-icon {
	display: block;
	width: var(--size-16);
	height: var(--size-16);
}

// Single-drug mobile frames (6547:5749): chips centred, list in text/black/primary.
.kova-v2-benefits__card_centered {
	.kova-v2-benefits__chips {
		align-self: center;
	}

	.kova-v2-benefits__item {
		color: var(--Text-Primary-100);
	}
}

.kova-v2-benefits__cta {
	display: none;
}

// Desktop (6547:17175): 60px block padding; 1200x564 card, copy left (48 padding),
// 600px visual right.
@media (min-width: 1024px) {
	.kova-v2-benefits {
		box-sizing: border-box;
		max-width: 1440px;
		margin-inline: auto;
		padding: var(--size-60) var(--size-40);
	}

	.kova-v2-benefits__card {
		display: grid;
		grid-template-columns: minmax(0, 1fr) minmax(0, 600px);
		grid-template-rows: auto auto auto 1fr;
		align-items: start;
		min-height: 564px; // 6547:17224
		padding: 0;

		border-radius: var(--size-36);
	}

	.kova-v2-benefits__visual {
		grid-column: 2;
		grid-row: 1 / -1;
		align-self: stretch;

		height: auto;
		min-height: 564px;
		margin: 0;
	}

	.kova-v2-benefits__mark {
		left: 44px;
		top: -76px;
		width: 602px;
		height: 680px;
	}

	// 6557:32798 — 317 wide at (0,160), 32 radius, 12/38/12/12 padding.
	.kova-v2-benefits__notification {
		left: 0;
		top: 160px;

		gap: var(--size-8);
		width: 317px;
		height: auto;
		padding: var(--size-12) var(--size-38) var(--size-12) var(--size-12);

		border-radius: var(--size-32);
	}

	// 6557:32799 — the same tail at 33.55x40: circles of 30.97 and 10.32.
	.kova-v2-benefits__tail {
		left: 168px;
		top: 84px;
		width: 33.55px;
		height: 40px;

		&::before {
			width: 30.97px;
			height: 30.97px;
		}

		&::after {
			left: 23.23px;
			top: 29.68px;
			width: 10.32px;
			height: 10.32px;
		}
	}

	.kova-v2-benefits__thumb {
		width: var(--size-64);
		height: var(--size-80);

		img {
			left: 0;
			top: 0;
			width: 100%;
			height: 100%;
			transform: none;
		}
	}

	.kova-v2-benefits__note-title {
		font-size: var(--size-16);
		font-weight: 400;
		line-height: var(--size-22); // 16px × 1.4 = 22.40px — exact size not in design system
		white-space: nowrap;

		color: var(--Text-Primary-100);
	}

	.kova-v2-benefits__note-text {
		font-size: var(--size-14);
		line-height: var(--size-18); // 14px × 1.32 = 18.48px — exact size not in design system
	}

	// 6557:32796 — 604x800 at (36,-12).
	.kova-v2-benefits__photo {
		left: 36px;
		top: -12px;
		width: 604px;
		height: 800px;
	}

	.kova-v2-benefits__visual .kova-v2-benefits__fade {
		display: none;
	}

	// 6547:17229 — 48 / 1.2, 48px from the card's top-left.
	.kova-v2-benefits__title {
		grid-column: 1;
		margin: var(--size-48) var(--size-8) 0 var(--size-48);

		font-size: var(--size-48);
		line-height: var(--size-56); // 48px × 1.2 = 57.60px — exact size not in design system
		letter-spacing: -0.96px;
	}

	.kova-v2-benefits__list {
		grid-column: 1;
		gap: var(--size-12);
		margin: var(--size-32) var(--size-8) 0 var(--size-48);
	}

	.kova-v2-benefits__item {
		font-size: var(--size-20);
		line-height: var(--size-28);
		font-weight: 400;
	}

	.kova-v2-benefits__bullet {
		width: var(--size-20);
		height: var(--size-20);
	}

	.kova-v2-benefits__chips {
		grid-column: 1;
		justify-self: start;
		margin: var(--size-32) var(--size-8) 0 var(--size-48);
	}

	// 6557:32785 — 16/8 padding, 12 radius, 20px icon, Inter Medium 20/24.
	.kova-v2-benefits__chip {
		gap: var(--size-8);
		padding: calc(var(--size-8) - 1px) calc(var(--size-16) - 1px); // 8/16 minus the inside 1px stroke

		font-size: var(--size-20);
		font-weight: 500;
		line-height: var(--size-24);

		border-radius: var(--size-12);
	}

	.kova-v2-benefits__chip-icon {
		width: var(--size-20);
		height: var(--size-20);
	}

	.kova-v2-benefits__card_centered {
		.kova-v2-benefits__chips {
			align-self: start;
		}

		.kova-v2-benefits__item {
			color: var(--kova-lp-ink);
		}
	}

	.kova-v2-benefits__cta {
		grid-column: 1;
		display: inline-flex;
		justify-self: start;
		// Figma's copy column overruns its 48px bottom padding (6547:17225 is 564 high
		// with 530px of content), so the button ends 34px above the card edge.
		margin: var(--size-40) var(--size-8) var(--size-32) var(--size-48);
	}
}

@media (min-width: 1440px) {
	.kova-v2-benefits {
		padding-inline: var(--size-120);
	}
}
</style>
